import React, { useState, useRef, useEffect } from 'react';
import dropdownArrow from '../../static/forms/dropdown-arrow.svg';

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  name: string;
  value: string;
  options: DropdownOption[];
  placeholder?: string;
  onChange: (name: string, value: string) => void;
  className?: string;
  maxHeight?: number;
}

const Dropdown: React.FC<DropdownProps> = ({
  name,
  value,
  options,
  placeholder = "-- Select option --",
  onChange,
  className = "",
  maxHeight = 220
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const displayValue = value 
    ? options.find(opt => opt.value === value)?.label 
    : placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Handle selecting an option
  const handleSelectOption = (selectedValue: string) => {
    onChange(name, selectedValue);
    setIsOpen(false);
  };

  return (
    <div 
      className={`custom-select-container ${className}`}
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: isOpen ? 20 : 10 }}
    >
      <div 
        className="form-input-container"
        onClick={toggleDropdown}
        style={{ 
          cursor: 'pointer',
          backgroundColor: isOpen ? '#f5f5f5' : 'white',
          borderRadius: isOpen ? '12px 12px 0 0' : '12px'
        }}
      >
        <div style={{ 
          padding: '14px 16px', 
          color: value ? '#333' : '#a3a3a3', 
          fontFamily: 'Montserrat, sans-serif',
          fontSize: '16px',
          fontWeight: value ? 600 : 500
        }}>
          {displayValue}
        </div>
        <img 
          src={dropdownArrow} 
          alt="" 
          className="dropdown-icon" 
          style={{
            transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
            transition: 'transform 0.2s ease'
          }}
        />
      </div>

      {isOpen && (
        <div className="dropdown-menu" style={{ 
          borderRadius: '0 0 12px 12px', 
          marginTop: '-1px',
          position: 'absolute',
          width: '100%',
          backgroundColor: '#f5f5f5',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 20,
          maxHeight: `${maxHeight}px`,
          overflowY: 'auto'
        }}>
          {options.map(option => (
            <div 
              key={option.value}
              className={`dropdown-option ${value === option.value ? 'selected' : ''}`}
              onClick={() => handleSelectOption(option.value)}
              style={{
                padding: '14px 16px',
                cursor: 'pointer',
                fontFamily: 'Montserrat, sans-serif',
                fontSize: '16px',
                fontWeight: value === option.value ? 600 : 500,
                backgroundColor: value === option.value ? '#6F2ECA' : 'transparent',
                color: value === option.value ? 'white' : '#333'
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown; 